import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import inventory from "@/api/inventory";
import inventoryPdf, { pdfErrorMessage } from "@/api/inventoryPdf";
import PdfExportButton from "./PdfExportButton";

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US");
}

function cellClass(v) {
  if (v === 0 || v === null || v === undefined) return "bg-transparent";
  const abs = Math.abs(v);
  if (v > 0) {
    if (abs < 5) return "bg-emerald-900/30 text-emerald-200";
    if (abs < 25) return "bg-emerald-800/50 text-emerald-100";
    if (abs < 100) return "bg-emerald-700/70 text-white";
    if (abs < 500) return "bg-emerald-600 text-white";
    return "bg-emerald-500 text-white font-semibold";
  }
  if (abs < 5) return "bg-rose-900/30 text-rose-200";
  if (abs < 25) return "bg-rose-800/50 text-rose-100";
  if (abs < 100) return "bg-rose-700/70 text-white";
  if (abs < 500) return "bg-rose-600 text-white";
  return "bg-rose-500 text-white font-semibold";
}

export default function InventoryMonthlyPivotTable({
  filters,
  testId = "monthly-pivot-table",
  apiOverride,
  hideExport = false,
}) {
  const { t } = useTranslation();
  const api = apiOverride || inventory;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("total_year");
  const [sortDir, setSortDir] = useState("desc");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], months_axis: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => { setPage(1); }, [filters?.preset, filters?.date_from, filters?.date_to, filters?.source_report, search]);

  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getMonthlyPivot(filters, { page, pageSize, sortBy, sortDir, search })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.pivotLoad", { defaultValue: "No se pudo cargar la tabla pivot" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, pageSize, sortBy, sortDir, search, t]);

  const months = data.months_axis || [];
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / pageSize)),
    [data.total, pageSize]
  );

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.downloadMonthlyPivotExport(filters, { sortBy, sortDir, search });
      toast.success(t("inventory.export.success", { defaultValue: "Excel descargado" }));
    } catch (e) {
      toast.error(t("inventory.export.error", { defaultValue: "Fallo al exportar Excel" }));
      console.error(e);
    } finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await inventoryPdf.monthlyPivot(filters, { sortBy, sortDir, search });
      toast.success(t("inventory.exportPdf.success", { defaultValue: "PDF descargado" }));
    } catch (e) {
      toast.error(pdfErrorMessage(e, t));
      console.error(e);
    } finally { setExportingPdf(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={t("inventory.searchPlaceholder", { defaultValue: "Buscar SKU o descripción..." })}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-7 h-8 text-sm"
            data-testid="pivot-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading} data-testid="pivot-export-btn" className={hideExport ? "hidden" : ""}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {t("inventory.exportExcel", { defaultValue: "Exportar Excel" })}
          </Button>
          <PdfExportButton
            onClick={handleExportPdf}
            exporting={exportingPdf}
            disabled={loading}
            hidden={hideExport}
            testId="pivot-pdf-btn"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto max-h-[560px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card border-b border-border z-10">
              <tr>
                <th
                  className="text-left p-2 cursor-pointer hover:bg-muted/40"
                  onClick={() => { setSortBy("sku"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                  data-testid="th-pivot-sku"
                >SKU</th>
                <th
                  className="text-left p-2 cursor-pointer hover:bg-muted/40"
                  onClick={() => { setSortBy("description"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                >Descripción</th>
                {months.map((m) => (
                  <th key={m} className="text-right p-2 tabular-nums font-medium text-muted-foreground">
                    {m}
                  </th>
                ))}
                <th
                  className="text-right p-2 cursor-pointer hover:bg-muted/40 sticky right-0 bg-card border-l border-border"
                  onClick={() => { setSortBy("total_year"); setSortDir((d) => d === "asc" ? "desc" : "asc"); }}
                  data-testid="th-pivot-total"
                >
                  {t("inventory.totalYear", { defaultValue: "Total" })}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && data.items.length === 0
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="p-2"><Skeleton className="h-3.5 w-20" /></td>
                      <td className="p-2"><Skeleton className="h-3.5 w-40" /></td>
                      {months.map((m) => <td key={m} className="p-2"><Skeleton className="h-3.5 w-12" /></td>)}
                      <td className="p-2"><Skeleton className="h-3.5 w-16" /></td>
                    </tr>
                  ))
                : data.items.length === 0
                  ? (
                    <tr>
                      <td colSpan={months.length + 3} className="text-center text-muted-foreground py-8">
                        {t("inventory.empty", { defaultValue: "Sin resultados" })}
                      </td>
                    </tr>
                  )
                  : data.items.map((row) => (
                    <tr key={row.sku} className="border-t border-border/60" data-testid={`pivot-row-${row.sku}`}>
                      <td className="p-2 font-mono">{row.sku}</td>
                      <td className="p-2 max-w-[200px] truncate">{row.description || "—"}</td>
                      {months.map((m) => {
                        const v = (row.months || {})[m] || 0;
                        return (
                          <td key={m} className={`p-2 text-right tabular-nums ${cellClass(v)}`}>
                            {v === 0 ? "·" : fmtNum(v)}
                          </td>
                        );
                      })}
                      <td className="p-2 text-right tabular-nums font-semibold sticky right-0 bg-card border-l border-border">
                        {fmtNum(row.total_year)}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          {((page - 1) * pageSize + 1).toLocaleString("en-US")}–{Math.min(page * pageSize, data.total).toLocaleString("en-US")} {t("inventory.of", { defaultValue: "de" })} {(data.total || 0).toLocaleString("en-US")}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="pivot-page-prev">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-2">{page} / {totalPages}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="pivot-page-next">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
